// Copyright (c) Docugami, Inc. All rights reserved.

import { ChoiceGroup, IChoiceGroupOption } from "@fluentui/react"
import { DgColoredButton, DgGreenColoredButtonProps } from "components/common/DgCommonStyledComponents"
import { dgFontFamily } from "components/common/Fonts"
import { emptyGray, greyText, homeGray, landingPageBackground, primaryTheme } from "components/shell/presentation/AppStyles"
import { useAppSelector } from "hooks/redux"
import { useMemo, useState } from "react"
import styled, { css } from "styled-components"
import { ReportSummaryPublishedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents'
import { Telemetry } from 'telemetry/TelemetryManager'
import { ReactComponent as ReportSummary } from '../../assets/reportSummary.svg'
import { selectDocumentsWithCpml } from "components/reports/AgGridReportTable"
import { selectClusterId, selectIsReportPublished, selectOrderedReportEntryIds, selectReportId, selectReportLabels, selectReviewFeedbackLockStats } from "store/reports/selectors"
import { reportSingleBatchSize, useGetPublishedDocumentsQuery, useGetReviewDocumentsQuery } from "store/reports/reportsSlice"

const BusyCss = css`
    cursor: wait;
    >div {
        opacity: .5;
        pointer-events: none;
    }
`

const Container = styled.div<{ isBusy?: boolean }>`
    font-family: ${dgFontFamily};
    flex-basis: 100%;
    display: flex;
    flex-direction: column;
    background: ${landingPageBackground};
    overflow-y: auto;
    ${props => props.isBusy && BusyCss}
    gap: 45px;
`

const Header = styled.div`
    flex-basis: 150px;
    display: flex;
    text-transform: uppercase;
    font-size: 42px;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid ${emptyGray};
`

const Title = styled.div`
    max-width: 65%;
    padding: 20px 0;
`

const Content = styled.div`
    flex: 1;
    display: flex;
    padding: 20px 0;
`

const ContentPanel = styled.div`
    flex-basis: 50%;
    display: flex;
    flex-direction: column;
    margin-left: auto;
    margin-right: 127px;
    gap: 25px;
`

const ContentPanelRow = styled.div`
    display: flex;
    font-size: 30px;
`

const ContentPanelRowLabel = styled.div`
    display: flex;
    text-transform: uppercase;
    color: ${greyText};
    font-weight: bold;
`

const ContentPanelRowData = styled.div`
    display: flex;
    margin-left: auto;
    font-weight: bold;
`

const StyledAutoConfirmationChoiceGroup = styled(ChoiceGroup)``

const Footer = styled.div`
    flex-basis: 50%;
    display: flex;
    padding: 20px 0;
    width: 100%;
`

const FooterPanel = styled.div`
    margin: 0 30px;
    width: 100%;
`

const PublishingNotes = styled.div`
    font-family: ${dgFontFamily};
    line-height: 36px;
    font-size: 20px;
    color: ${homeGray};
    white-space: pre-line;
`

const PublishingCommands = styled.div`
    margin-top: 26px;
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    gap: 5px;
`

const PublishButton = styled(DgColoredButton)`
    padding: 5px;
    text-transform: uppercase;
    width: 170px;
    height: 50px;
`

const BackButton = styled.div`
    width: 250px;
    cursor: pointer;
    color: ${greyText};
    font-size: 16px;
    :hover {
        text-decoration: underline;
        :active {
            color: ${primaryTheme.palette.neutralDark};
        }
    }
`

const StyledReportSummary = styled(ReportSummary)`
    width: 362px;
    height: 100%;
    align-self: center;
    justify-self: center;
`

const options: IChoiceGroupOption[] = [
    { key: 'true', text: 'Auto-confirm documents' },
    { key: 'false', text: 'Manually confirm documents' },
];

interface PublishConfirmationProps {
    onConfirm: (autoConfirm: boolean) => void;
    onCancel: () => void;
    autoPublish: boolean;
    isBusy?: boolean;
}

export const PublishConfirmation = ({ onConfirm, onCancel, autoPublish, isBusy }: PublishConfirmationProps) => {
    const [autoConfirm, setAutoConfirm] = useState(autoPublish);

    const clusterId = useAppSelector(selectClusterId);
    const reportId = useAppSelector(selectReportId);
    const entryCount = useAppSelector(selectOrderedReportEntryIds)?.length ?? 0;
    const reportLabels = useAppSelector(selectReportLabels);
    const isInitialPublish = !useAppSelector(selectIsReportPublished);

    const reviewBindingStats = useAppSelector(selectReviewFeedbackLockStats);
    const publishedDocumentsCount = useGetPublishedDocumentsQuery(reportId).data?.documents.ids.length;
    const reviewDocumentIds = useGetReviewDocumentsQuery(reportId).data?.documents.ids;

    const docSetDocumentsCount = useAppSelector(state => selectDocumentsWithCpml(state, clusterId)?.ids.length);

    const documentsToPublishCount = useMemo(() => {
        return Object.entries(reviewBindingStats?.documentsAllLocked || {})
            .filter(([folderId, confirmed]) => reviewDocumentIds?.includes(folderId) && confirmed).length;
    }, [reviewBindingStats?.documentsAllLocked, reviewDocumentIds])

    function onPublishConfirm() {
        Telemetry.trackEvent<ReportSummaryPublishedSchema>({
            eventName: TelemetryEvent.ReportSummaryPublished
        });
        onConfirm(autoConfirm);
    }

    function getPublishingMessage() {
        if (autoConfirm) {
            if (isInitialPublish) {
                return (
                    <PublishingNotes>
                        Publishing will lock the {reportLabels.itemsName} items.
                        <br />
                        {`Docugami will automatically confirm and publish the remaining documents in the doc set as well as any future documents as they are added.
                        Once published, you can add or remove items by going to`} <b>Options &gt; Edit {reportLabels.titleName.toLocaleLowerCase()}</b>.
                    </PublishingNotes>
                )
            }
            else {
                return (
                    <PublishingNotes>
                        {`Docugami will automatically confirm and publish the remaining documents in the doc set as well as any future documents as they are added.
                        You can add or remove items by going to`} <b>Options &gt; Edit {reportLabels.titleName.toLocaleLowerCase()}</b>.
                    </PublishingNotes>
                )
            }
        }
        // Manual confirmation
        else if (isInitialPublish) {
            return (
                <PublishingNotes>
                    Publishing will lock the {reportLabels.itemsName} items.
                    <br />
                    {`Confirm an additional batch of ${reportSingleBatchSize} documents to improve your results. After fully confirming the batch, you can continue to train on another batch, or switch to auto-confirm.
                    Once published, you can add or remove items by going to `}
                    <b>Options &gt; Edit {reportLabels.titleName.toLocaleLowerCase()}</b>.
                </PublishingNotes>
            )
        }
        else {
            return (
                <PublishingNotes>
                    {`Confirm an additional batch of ${reportSingleBatchSize} documents to improve your results. After fully confirming the batch, you can continue to train on another batch, or switch to auto-confirm.
                    You can add or remove items by going to `}
                    <b>Options &gt; Edit {reportLabels.titleName.toLocaleLowerCase()}</b>.
                </PublishingNotes>
            )
        }
    }

    const remainingCount = publishedDocumentsCount !== undefined && !!docSetDocumentsCount ? docSetDocumentsCount - publishedDocumentsCount - documentsToPublishCount : -1;
    return (
        <Container isBusy={isBusy}>
            <Header>
                <Title>
                    {`${reportLabels.titleName} Summary`}
                </Title>
            </Header>
            <Content>
                <ContentPanel>
                    <StyledReportSummary />
                </ContentPanel>
                <ContentPanel>
                    <ContentPanelRow>
                        <ContentPanelRowLabel>Columns in {reportLabels.titleName}:</ContentPanelRowLabel>
                        <ContentPanelRowData>{entryCount}</ContentPanelRowData>
                    </ContentPanelRow>
                    <ContentPanelRow>
                        <ContentPanelRowLabel>documents to publish:</ContentPanelRowLabel>
                        <ContentPanelRowData>{documentsToPublishCount}</ContentPanelRowData>
                    </ContentPanelRow>
                    <ContentPanelRow>
                        <ContentPanelRowLabel>published documents:</ContentPanelRowLabel>
                        <ContentPanelRowData>{publishedDocumentsCount}</ContentPanelRowData>
                    </ContentPanelRow>
                    {
                        remainingCount >= 0 && (
                            <ContentPanelRow>
                                <ContentPanelRowLabel>remaining documents:</ContentPanelRowLabel>
                                <ContentPanelRowData>{remainingCount}</ContentPanelRowData>
                            </ContentPanelRow>
                        )
                    }
                    <ContentPanelRow>
                        <ContentPanelRowLabel>auto-confirm documents:</ContentPanelRowLabel>
                    </ContentPanelRow>
                    <ContentPanelRow>
                        <StyledAutoConfirmationChoiceGroup
                            styles={{
                                root: {
                                    'label': {
                                        'font-size': '24px',
                                        ':before': {
                                            'margin-top': '6px'
                                        },
                                        ':after': {
                                            'margin-top': '6px'
                                        }
                                    },
                                },
                            }}
                            options={options}
                            selectedKey={String(autoConfirm)}
                            onChange={(ev, option) => setAutoConfirm(option?.key === 'true')} />
                    </ContentPanelRow>
                </ContentPanel>
            </Content>
            <Footer>
                <FooterPanel>
                    {getPublishingMessage()}
                    <PublishingCommands>
                        <PublishButton controlButtonProps={DgGreenColoredButtonProps} onClick={() => onPublishConfirm()}>PUBLISH</PublishButton>
                        <BackButton onClick={onCancel}>Return to {reportLabels.titleName} Designer</BackButton>
                    </PublishingCommands>
                </FooterPanel>
            </Footer>
        </Container>
    )
}
